import { useCallback, useMemo } from "react"

import { useSurfacePreference } from "../../../hooks/use-surface-preference.ts"
import { surfaceKey } from "../../User/redux/ui-preferences.types.ts"

export interface GuideProgress {
  completedLessonIds: string[]
  completedTourIds: string[]
  /** REVIEW-type steps (Permissions, Notifications) the user has explicitly
   * acknowledged via "I've reviewed ... →" — never means the underlying
   * configuration is "correct," only that the user looked at it. */
  reviewedStepIds: string[]
  /** OPTIONAL-type steps (Templates) the user chose to defer via
   * "Skip for now" — a skipped step still counts as handled for setup
   * completion purposes, it's just not been acted on yet. */
  skippedStepIds: string[]
  /** Manually toggled Setup Assistant checklist rows ("I reviewed/did
   * this"), keyed `${stepId}.${subStepId}`. Purely a personal tracking aid
   * — never read by `isOnboardingItemComplete`/`computeSetupProgress`. A
   * step whose real completion is backed by actual product data (Company,
   * Team, Templates) uses that signal regardless of what's checked here;
   * this exists for the rows that have no such signal (e.g. "Company
   * logo," "Review your information"). */
  checkedGuideItemIds: string[]
}

const DEFAULT_PROGRESS: GuideProgress = {
  completedLessonIds: [],
  completedTourIds: [],
  reviewedStepIds: [],
  skippedStepIds: [],
  checkedGuideItemIds: []
}

const GUIDE_PROGRESS_KEY = surfaceKey("guide.progress")

const withToggled = (ids: string[], id: string, complete: boolean): string[] =>
  complete
    ? ids.includes(id)
      ? ids
      : [...ids, id]
    : ids.filter((existing) => existing !== id)

export const useGuideProgress = () => {
  const [storedProgress, setProgress] = useSurfacePreference<GuideProgress>(
    GUIDE_PROGRESS_KEY,
    DEFAULT_PROGRESS
  )
  // The fallback above only applies when nothing is stored at all — an
  // account with progress saved before reviewedStepIds/skippedStepIds
  // existed (this surface previously stored completedChecklistItemIds
  // instead) still reads back that old shape verbatim, missing the new
  // keys entirely. Merge over the default so a legacy or partial stored
  // value can never crash a `.includes` call downstream.
  // Memoized on `storedProgress` alone: every setter below closes over
  // `progress`, and this hook is called from a provider mounted app-wide
  // (`GuideOnboardingAssistantContext`) — a fresh object identity here on
  // every render defeated that provider's own `value` memo on every route
  // change, not just an actual progress change.
  const progress: GuideProgress = useMemo(
    () => ({ ...DEFAULT_PROGRESS, ...storedProgress }),
    [storedProgress]
  )

  const setLessonComplete = useCallback(
    (lessonId: string, complete = true) => {
      setProgress({
        ...progress,
        completedLessonIds: withToggled(
          progress.completedLessonIds,
          lessonId,
          complete
        )
      })
    },
    [progress, setProgress]
  )

  const setTourComplete = useCallback(
    (tourId: string, complete = true) => {
      setProgress({
        ...progress,
        completedTourIds: withToggled(progress.completedTourIds, tourId, complete)
      })
    },
    [progress, setProgress]
  )

  const setStepReviewed = useCallback(
    (stepId: string, reviewed = true) => {
      setProgress({
        ...progress,
        reviewedStepIds: withToggled(progress.reviewedStepIds, stepId, reviewed)
      })
    },
    [progress, setProgress]
  )

  const setStepSkipped = useCallback(
    (stepId: string, skipped = true) => {
      setProgress({
        ...progress,
        skippedStepIds: withToggled(progress.skippedStepIds, stepId, skipped)
      })
    },
    [progress, setProgress]
  )

  const setGuideItemChecked = useCallback(
    (itemId: string, checked: boolean) => {
      setProgress({
        ...progress,
        checkedGuideItemIds: withToggled(progress.checkedGuideItemIds, itemId, checked)
      })
    },
    [progress, setProgress]
  )

  return {
    progress,
    isLessonComplete: (lessonId: string) =>
      progress.completedLessonIds.includes(lessonId),
    isTourComplete: (tourId: string) =>
      progress.completedTourIds.includes(tourId),
    isGuideItemChecked: (itemId: string) =>
      progress.checkedGuideItemIds.includes(itemId),
    setLessonComplete,
    setTourComplete,
    setStepReviewed,
    setStepSkipped,
    setGuideItemChecked
  }
}
